import { areArraysEqual } from '@mui/base/utils/areArraysEqual';

export const hasOne = <T>(arr: readonly T[]): arr is [T, ...T[]] => arr.length >= 1;

export const hasExactlyOne = <T>(arr: readonly T[]): arr is [T] => arr.length === 1;

/**
* Create an object with properties keyed by the result of calling a given
*   function on each item of an array.
* @param array - The array of items to create the object with.
* @param keyFn - The function that returns the key by which to group the object's properties.
* @returns An object with the properties keyed by the result of calling the
*   function on each item of the array.
* @example
* keyBy(
    [{id: 1, name: 'John'}, {id: 2, name: 'Doe'}, {id: 3, name: 'Jane'}],
    (item) => item.id
)
// {1: {id: 1, name: 'John'}, 2: {id: 2, name: 'Doe'}, 3: {id: 3, name: 'Jane'}}
*/
export const keyBy = <TItem, TKey extends string | number = string>(
  array: readonly TItem[],
  keyFn: (item: TItem) => TKey,
) => Object.fromEntries(array.map((item) => [keyFn(item), item])) as Record<TKey, TItem>;

type Interval = readonly [number, number];

// source: https://javascript.plainenglish.io/merge-intervals-98da240805bc
export const mergeIntervals = (intervals: readonly Interval[]) => {
  if (intervals.length < 2) {
    return intervals;
  }

  const sortedIntervals = intervals.slice(0).sort((a, b) => a[0] - b[0]);

  const result = [];
  let previous = sortedIntervals[0]!;

  for (let i = 1; i < sortedIntervals.length; i += 1) {
    if (previous[1] >= sortedIntervals[i]![0]) {
      previous = [previous[0], Math.max(previous[1], sortedIntervals[i]![1])];
    } else {
      result.push(previous);
      previous = sortedIntervals[i]!;
    }
  }

  result.push(previous);

  return result;
};

export const uniq = <T>(array: readonly T[]): T[] => [...new Set(array)];

export const uniqBy = <T>(array: readonly T[], keyFn: (item: T) => string | number): T[] => [
  ...new Map(array.map((item) => [keyFn(item), item])).values(),
];

/**
 * Create a new array with the items that only exist in the first array.
 * @param first - The first array.
 * @param second - The second array.
 * @returns A new array containing only the items that exist only in the first array.
 * @example
 * difference([1, 2, 3], [1, 2, 4]) // [3]
 */
export const difference = <T>(first: readonly T[], second: readonly T[]): T[] =>
  first.filter((item) => !second.includes(item));

/**
 * Create an object with keys derived from the items in an array and values
 * representing the frequency of each key.
 * @param array - The input array.
 * @param keyFn - A function to extract the key from each array item.
 * @returns An object containing the frequency of each key.
 * @example
 * countBy(['John', 'Jane', 'Jane', 'Jack', 'Jack'], name => name.substring(0, 2))
 *  // { Jo: 1, Ja: 4 }
 */
export const countBy = <TItem, TKey extends string | number = string>(
  array: readonly TItem[],
  keyFn: (item: TItem) => TKey,
) => {
  const result = {} as Record<TKey, number>;

  for (const item of array) {
    const key = keyFn(item);
    result[key] = (result[key] || 0) + 1;
  }

  return result;
};

/**
 * Create an object with keys derived from the items in an array and values
 * being arrays of items that share the same key.
 * @param array - The input array.
 * @param keyFn - A function to extract the key from each array item.
 * @returns An object containing arrays of items that share the same key.
 * @example
 * groupBy(['John', 'Jane', 'Jane', 'Jack', 'Jack'], name => name.charAt(0))
 *  // { J: ['John', 'Jane', 'Jane', 'Jack', 'Jack'] }
 */
export const groupBy = <TItem>(
  array: readonly TItem[],
  keyFn: (item: TItem) => string | number,
) => {
  const result = {} as Record<string, TItem[]>;

  for (const item of array) {
    const key = keyFn(item);
    result[key] = [...(result[key] ?? []), item];
  }

  return result;
};

export const groupSafelyBy = <TItem, TKey extends string | number = string>(
  array: readonly TItem[],
  keyFn: (item: TItem) => TKey,
) => groupBy(array, keyFn) as Partial<Record<TKey, TItem[]>>;

const compareMultiple = (a: any, b: any, orders: Array<'asc' | 'desc'>) => {
  for (let i = 0; i < a.criteria.length; i += 1) {
    const order = orders[i] || 'asc';
    const sign = order === 'asc' ? 1 : -1;
    if (a.criteria[i] < b.criteria[i]) {
      return sign * -1;
    }
    if (a.criteria[i] > b.criteria[i]) {
      return sign;
    }
  }
  return a.index - b.index;
};

/**
 * Sort the elements of an array in order of the provided iteratees,
 * which can be a property name, or a custom iteratee function.
 * @param array - The array to be sorted.
 * @param iteratees - The property name or custom iteratee function that is used to sort the array.
 * @param orders - The order of sorting, can be 'asc' or 'desc'.
 * @returns A new sorted array.
 * @example
 * orderBy([
 *   {name: 'Jane', age: 25},
 *   {name: 'Jane', age: 20},
 *   {name: 'Jack', age: 30},
 * ],
 * [({name}) => name.toLocaleLowerCase(), ({age}) => age],
 * ['asc', 'desc'])
 *  // [{name: 'Jack', age: 30}, {name: 'Jane', age: 25}, {name: 'Jane', age: 20}]
 */
export const orderBy = <TItem>(
  array: readonly TItem[],
  iteratees: Array<(item: TItem) => any>,
  orders: Array<'asc' | 'desc'>,
) => {
  const result = array.map((item, index) => ({
    criteria: iteratees.map((iteratee) => iteratee(item)),
    index,
    value: item,
  }));

  result.sort((a, b) => compareMultiple(a, b, orders));

  return result.map((item) => item.value);
};

// no point re-implementing this, but we should import uses from our own utils
export const shallowEqual = areArraysEqual as <T>(a: readonly T[], b: readonly T[]) => boolean;

/**
 * Find the maximum value in an array. Relies on the item's comparison operator.
 * @param array - The array to find the maximum value in.
 * @returns The maximum value in the array.
 * @example
 * max([1, 2, 3, 4, 5]) // 5
 * max(['a', 'b', 'c', 'd', 'e']) // 'e'
 * max([]) // undefined
 */
export const max = <TItem>(
  array: readonly TItem[],
): typeof array extends [TItem, ...TItem[]] ? TItem : TItem | undefined =>
  array.reduce((acc, item) => (item <= acc! ? acc : item), array[0]);

/**
 * Determines if the provided value is one of the values in the provided array.
 * @param value - The value to check.
 * @param arr - The array of values to check against.
 * @returns `true` if the value is one of the values in the array, `false` otherwise.
 * @example
 * isOneOf('a', ['a', 'b', 'c']) // true
 * isOneOf('d', ['a', 'b', 'c']) // false
 */
export const isOneOf = <const T extends readonly any[]>(
  value: unknown,
  arr: T,
): value is T[number] => arr.includes(value as any);
